import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import apiClient from '@services/apiClient';
import { clinicSettingsService } from '@services/clinicSettingsService';
import { useAuth } from '@contexts/AuthContext';

type SimpleUser = {
  id: string;
  firstName?: string;
  lastName?: string;
  email: string;
  role: string;
  status: string;
};

type GdprRequest = {
  id: string;
  type: 'export' | 'deletion' | string;
  status: string;
  createdAt: string;
  processedAt?: string;
  errorMessage?: string;
  exportFilename?: string;
};

const AdministrationManagement: React.FC = () => {
  const { user } = useAuth();
  const clinicId = user?.clinicId || '';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saveMessage, setSaveMessage] = useState('');

  const [users, setUsers] = useState<SimpleUser[]>([]);
  const [auditRows, setAuditRows] = useState<Array<{ id: string; userName: string; action: string; resource: string; timestamp: string }>>([]);
  const [gdprRequests, setGdprRequests] = useState<GdprRequest[]>([]);

  const [integrationState, setIntegrationState] = useState({
    backupEnabled: false,
    backupFrequency: 'daily',
    apiRateLimit: 1000,
  });
  const [gdprState, setGdprState] = useState({
    allowDataExport: true,
    allowDataDeletion: true,
    contactEmail: 'dpo@clinic.ro',
  });

  const canLoad = useMemo(() => Boolean(clinicId), [clinicId]);

  const loadData = useCallback(async () => {
    if (!canLoad) return;
    setLoading(true);
    setError('');
    setSaveMessage('');
    try {
      const [usersRes, integrations, gdpr, audit, requests] = await Promise.all([
        apiClient.get('/users'),
        clinicSettingsService.getIntegrationSettings(clinicId),
        clinicSettingsService.getGDPRSettings(clinicId),
        clinicSettingsService.getAuditLogs(clinicId, { page: 1, limit: 8 }),
        apiClient.get('/gdpr/requests'),
      ]);

      setUsers(usersRes.data?.data || []);
      setAuditRows(audit.logs || []);
      setGdprRequests(requests.data?.data || []);
      setIntegrationState({
        backupEnabled: Boolean((integrations as any)?.backupSettings?.enabled),
        backupFrequency: String((integrations as any)?.backupSettings?.frequency || 'daily'),
        apiRateLimit: Number((integrations as any)?.apiSettings?.rateLimit || 1000),
      });
      setGdprState({
        allowDataExport: Boolean((gdpr as any)?.rights?.allowDataExport),
        allowDataDeletion: Boolean((gdpr as any)?.rights?.allowDataDeletion),
        contactEmail: String((gdpr as any)?.breachNotification?.contactEmail || 'dpo@clinic.ro'),
      });
    } catch (err) {
      console.error('Eroare la încărcarea administrării:', err);
      setError('Nu am putut încărca datele administrative.');
    } finally {
      setLoading(false);
    }
  }, [canLoad, clinicId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const saveIntegrations = async () => {
    if (!canLoad) return;
    setSaveMessage('');
    try {
      await clinicSettingsService.updateIntegrationSettings(clinicId, {
        backupSettings: { enabled: integrationState.backupEnabled, frequency: integrationState.backupFrequency },
        apiSettings: { enabled: true, rateLimit: integrationState.apiRateLimit },
      } as any);
      setSaveMessage('Setările de integrări au fost salvate.');
    } catch (err) {
      console.error(err);
      setError('Eroare la salvarea setărilor de integrări.');
    }
  };

  const saveGdpr = async () => {
    if (!canLoad) return;
    setSaveMessage('');
    try {
      await clinicSettingsService.updateGDPRSettings(clinicId, {
        rights: {
          allowDataExport: gdprState.allowDataExport,
          allowDataDeletion: gdprState.allowDataDeletion,
          allowDataPortability: true,
          allowDataRectification: true,
        },
        breachNotification: { enabled: true, notificationPeriod: 72, contactEmail: gdprState.contactEmail },
      } as any);
      setSaveMessage('Setările GDPR au fost salvate.');
    } catch (err) {
      console.error(err);
      setError('Eroare la salvarea setărilor GDPR.');
    }
  };

  const triggerExport = async () => {
    try {
      await apiClient.post('/gdpr/export', {});
      await loadData();
      setSaveMessage('Cererea GDPR export a fost înregistrată.');
    } catch (err) {
      console.error(err);
      setError('Nu am putut porni exportul GDPR.');
    }
  };

  const triggerDeletion = async () => {
    try {
      await apiClient.post('/gdpr/delete', { confirmation: 'DELETE_MY_DATA_PERMANENTLY' });
      await loadData();
      setSaveMessage('Cererea GDPR de ștergere/anonimizare a fost înregistrată.');
    } catch (err) {
      console.error(err);
      setError('Nu am putut porni cererea de ștergere GDPR.');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={600} gutterBottom>
          Administrare
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Modulul este conectat la API-uri reale pentru utilizatori, audit, integrări și GDPR.
        </Typography>
      </Box>

      {!canLoad && <Alert severity="warning">Nu am găsit clinicId în sesiune. Reautentifică-te ca clinic admin.</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {saveMessage && <Alert severity="success" sx={{ mb: 2 }}>{saveMessage}</Alert>}

      {loading ? (
        <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1.5 }}>
                  Utilizatori clinică
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nume</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Rol</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.slice(0, 8).map((u) => (
                      <TableRow key={u.id}>
                        <TableCell>{`${u.firstName || ''} ${u.lastName || ''}`.trim() || '-'}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>{u.role}</TableCell>
                        <TableCell>{u.status}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1.5 }}>
                  Audit administrativ
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Actor</TableCell>
                      <TableCell>Acțiune</TableCell>
                      <TableCell>Resursă</TableCell>
                      <TableCell>Moment</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {auditRows.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>{row.userName}</TableCell>
                        <TableCell>{row.action}</TableCell>
                        <TableCell>{row.resource}</TableCell>
                        <TableCell>{new Date(row.timestamp).toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1.5 }}>
                  Integrări clinică
                </Typography>
                <Stack spacing={1.5}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={integrationState.backupEnabled}
                        onChange={(e) => setIntegrationState((prev) => ({ ...prev, backupEnabled: e.target.checked }))}
                      />
                    }
                    label="Backup automat activ"
                  />
                  <TextField
                    label="Frecvență backup"
                    value={integrationState.backupFrequency}
                    onChange={(e) => setIntegrationState((prev) => ({ ...prev, backupFrequency: e.target.value }))}
                    size="small"
                  />
                  <TextField
                    label="API rate limit"
                    type="number"
                    value={integrationState.apiRateLimit}
                    onChange={(e) => setIntegrationState((prev) => ({ ...prev, apiRateLimit: Number(e.target.value || 0) }))}
                    size="small"
                  />
                  <Button variant="contained" onClick={saveIntegrations}>
                    Salvează integrări
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1.5 }}>
                  GDPR clinică
                </Typography>
                <Stack spacing={1.5}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={gdprState.allowDataExport}
                        onChange={(e) => setGdprState((prev) => ({ ...prev, allowDataExport: e.target.checked }))}
                      />
                    }
                    label="Permite export date"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={gdprState.allowDataDeletion}
                        onChange={(e) => setGdprState((prev) => ({ ...prev, allowDataDeletion: e.target.checked }))}
                      />
                    }
                    label="Permite ștergere/anonymizare"
                  />
                  <TextField
                    label="Email DPO"
                    type="email"
                    value={gdprState.contactEmail}
                    onChange={(e) => setGdprState((prev) => ({ ...prev, contactEmail: e.target.value }))}
                    size="small"
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <Button variant="contained" onClick={saveGdpr}>
                      Salvează GDPR
                    </Button>
                    <Button variant="outlined" onClick={triggerExport}>
                      Solicită export
                    </Button>
                    <Button variant="outlined" color="warning" onClick={triggerDeletion}>
                      Solicită ștergere
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Card sx={{ borderRadius: 2, mt: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
            Istoric cereri GDPR
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tip</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Creat la</TableCell>
                <TableCell>Procesat la</TableCell>
                <TableCell>Eroare</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {gdprRequests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>Nu există cereri GDPR.</TableCell>
                </TableRow>
              ) : (
                gdprRequests.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.type}</TableCell>
                    <TableCell>{item.status}</TableCell>
                    <TableCell>{new Date(item.createdAt).toLocaleString()}</TableCell>
                    <TableCell>{item.processedAt ? new Date(item.processedAt).toLocaleString() : '-'}</TableCell>
                    <TableCell>{item.errorMessage || '-'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AdministrationManagement;
